// LoanDetails.jsx — Mortgage Matrix (Refinance Flow) — v9.7
import React, { useState, useEffect } from "react";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import Select from "react-select";
import styles from "./RefinanceForm.module.css";

// 🔥 Correct LIVE lookup endpoint (NO "-API")
const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile";

function LoanDetails({ formData, setFormData, nextStep }) {
  const [addressValue, setAddressValue] = useState(
    formData.propertyAddress?.formatted
      ? {
          label: formData.propertyAddress.formatted,
          value: formData.propertyAddress.formatted,
        }
      : null
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------- Helper: Normalise data from Lambda ----------
  const normaliseLeadData = (data) => {
    const formatAddress = (addr) => {
      if (!addr) return "";
      return addr
        .toString()
        .replace(/\s*,\s*/g, ", ")
        .replace(/^,|,$/g, "")
        .replace(/QLD,\s*$/, "QLD")
        .trim();
    };

    const rawRate = data.interestRate;
    const defaultRate =
      rawRate === null ||
      rawRate === undefined ||
      rawRate === 0 ||
      rawRate === "0"
        ? "6.00"
        : (() => {
            const n = parseFloat(rawRate);
            return isNaN(n) ? "" : n.toFixed(2);
          })();

    const mortgageLoans = Array.isArray(data.mortgageLoans)
      ? data.mortgageLoans.map((loan) => ({
          creditor: loan.creditor || loan.institution || "",
          limit: loan.limit || loan.balance || "",
          interestRate:
            typeof loan.interestRate === "number"
              ? loan.interestRate
              : loan.interestRate
              ? parseFloat(loan.interestRate)
              : "",
        }))
      : [];

    const mainLoan = mortgageLoans[0] || {};

    return {
      propertyAddress: formatAddress(data.propertyAddress),
      propertyValue: data.propertyValue || "",
      loanBalance: data.loanBalance || mainLoan.limit || "",
      interestRate: defaultRate,
      currentLender: data.currentLender || mainLoan.creditor || "",
      mortgageLoans,
    };
  };

  // ✅ Auto-fetch property & loan data via Lambda
  useEffect(() => {
    const mobile = formData?.applicant1?.phone || formData?.phone;
    if (!mobile) return;

    const fetchLead = async () => {
      try {
        setLoading(true);
        const res = await fetch(LOOKUP_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mobile }),
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (err) {
          console.error("❌ Lead JSON parse error:", text);
          return;
        }

        if (!res.ok || !data.success || !data.found) {
          console.warn("ℹ️ No existing property/loan details for this client.");
          return;
        }

        const {
          propertyAddress,
          propertyValue,
          loanBalance,
          interestRate,
          currentLender,
          mortgageLoans,
        } = normaliseLeadData(data);

        setFormData((prev) => ({
          ...prev,
          propertyAddress: {
            formatted: propertyAddress || "",
          },
          propertyValue: propertyValue || "",
          loanBalance: loanBalance || "",
          interestRate: interestRate || "",
          currentLender: currentLender || prev.currentLender || "",
          // 🔁 Pass all mortgage loans forward so Liabilities step can use them
          mortgageLoans: mortgageLoans || [],
        }));

        if (propertyAddress) {
          setAddressValue({
            label: propertyAddress,
            value: propertyAddress,
          });
        }
      } catch (err) {
        console.error("❌ Error fetching lead:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Format numbers with commas
  const formatNumber = (value) => {
    if (!value) return "";
    const num = value.toString().replace(/,/g, "");
    return isNaN(num) ? "" : Number(num).toLocaleString("en-AU");
  };

  const parseNumber = (value) =>
    value ? value.toString().replace(/,/g, "") : "";

  const handleChange = (field, value) => {
    const rawValue = parseNumber(value);
    setFormData((prev) => ({ ...prev, [field]: rawValue }));
  };

  // ✅ Handle Google address selection
  const handleAddressSelect = async (selected) => {
    if (!selected) return;
    setAddressValue(selected);

    try {
      const results = await geocodeByPlaceId(selected.value.place_id);
      const place = results[0];
      const components = place.address_components;

      const get = (type) =>
        components.find((c) => c.types.includes(type))?.long_name || "";

      const formatted = place.formatted_address || selected.label;
      const street = `${get("street_number")} ${get("route")}`.trim();
      const suburb =
        get("locality") ||
        get("sublocality") ||
        get("administrative_area_level_2");
      const state = get("administrative_area_level_1");
      const postcode = get("postal_code");

      const structuredAddress = { street, suburb, state, postcode, formatted };

      setFormData((prev) => ({
        ...prev,
        propertyAddress: structuredAddress,
      }));
    } catch (error) {
      console.error("❌ Error parsing address:", error);
      setFormData((prev) => ({
        ...prev,
        propertyAddress: { formatted: selected.label },
      }));
    }
  };

  // ✅ Interest rate formatting
  const handleInterestRateChange = (value) => {
    let cleaned = value.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2 || parts[0].length > 2) return;
    if (parts[1] && parts[1].length > 2) return;

    const num = parseFloat(cleaned);
    if (!isNaN(num) && num <= 99.99) {
      setFormData((prev) => ({ ...prev, interestRate: cleaned }));
    } else if (!cleaned) {
      setFormData((prev) => ({ ...prev, interestRate: "" }));
    }
  };

  const formatInterestRateOnBlur = () => {
    let rate = formData.interestRate;
    if (!rate) return;
    let num = parseFloat(rate);
    if (!isNaN(num)) {
      const formatted = num.toFixed(2);
      setFormData((prev) => ({ ...prev, interestRate: formatted }));
    }
  };

  // ✅ Calculate LVR
  const propertyValueNum = parseFloat(formData.propertyValue || 0);
  const loanBalanceNum = parseFloat(formData.loanBalance || 0);
  const lvr =
    propertyValueNum > 0
      ? ((loanBalanceNum / propertyValueNum) * 100).toFixed(1)
      : null;

  // ✅ Lender options
  const lenderOptions = [
    "CBA",
    "Suncorp",
    "Bankwest",
    "ME Bank",
    "ANZ",
    "Macquarie Bank",
    "AMP",
    "Pepper Money",
    "Westpac",
    "NAB",
    "St George Bank",
    "Firstmac",
    "Other",
  ].map((v) => ({ value: v, label: v }));

  // ✅ Validation watcher
  useEffect(() => {
    const valid =
      formData.propertyAddress?.formatted &&
      formData.propertyValue &&
      formData.loanBalance &&
      formData.interestRate &&
      formData.currentLender;
    setIsFormValid(!!valid);
  }, [formData]);

  const handleNext = () => {
    setSubmitted(true);
    if (isFormValid) {
      nextStep();
    } else {
      alert("⚠️ Please complete all required fields before continuing.");
    }
  };

  return (
    <div className={styles.container}>
      <h3>Property & Loan Details</h3>

      {loading && (
        <p className={styles.loadingText}>🔄 Loading your details...</p>
      )}

      {/* 🏠 Property Address */}
      <div className={styles.inputGroup}>
        <label>
          What is your property address?{" "}
          <span className={styles.required}>*</span>
        </label>
        <GooglePlacesAutocomplete
          apiKey="AIzaSyAUjzKOLtrcZlyh0l_XqZdrvYSlwOE_hNg"
          withSessionToken
          selectProps={{
            value: addressValue,
            onChange: handleAddressSelect,
            placeholder: "e.g. 12 Smith Street, Brisbane QLD 4000",
            menuPortalTarget:
              typeof document !== "undefined" ? document.body : null,
            styles: {
              control: (provided) => ({
                ...provided,
                borderRadius: "0.6rem",
                border:
                  submitted && !formData.propertyAddress?.formatted
                    ? "1.5px solid red"
                    : "1.5px solid #ccc",
                boxShadow: "none",
                minHeight: "3.25rem",
              }),
              menuPortal: (provided) => ({ ...provided, zIndex: 4000 }),
            },
          }}
        />
      </div>

      {/* 💰 Property Value */}
      <div className={styles.inputGroup}>
        <label>
          Estimated Property Value{" "}
          <span className={styles.required}>*</span>
        </label>
        <div className={styles.currencyInput}>
          <span className={styles.prefix}>$</span>
          <input
            type="text"
            value={formatNumber(formData.propertyValue)}
            onChange={(e) => handleChange("propertyValue", e.target.value)}
            placeholder="600,000"
            className={
              submitted && !formData.propertyValue ? styles.errorInput : ""
            }
          />
        </div>
      </div>

      {/* 💸 Loan Balance */}
      <div className={styles.inputGroup}>
        <label>
          Current Loan Balance <span className={styles.required}>*</span>
        </label>
        <div className={styles.currencyInput}>
          <span className={styles.prefix}>$</span>
          <input
            type="text"
            value={formatNumber(formData.loanBalance)}
            onChange={(e) => handleChange("loanBalance", e.target.value)}
            placeholder="400,000"
            className={
              submitted && !formData.loanBalance ? styles.errorInput : ""
            }
          />
        </div>
        {lvr && (
          <p className={styles.helperText}>
            Approx. LVR: <strong>{lvr}%</strong>
          </p>
        )}
      </div>

      {/* 📈 Interest Rate */}
      <div className={styles.inputGroup}>
        <label>
          Current Interest Rate <span className={styles.required}>*</span>
        </label>
        <div className={styles.currencyInputWithSuffix}>
          <input
            type="text"
            value={formData.interestRate || ""}
            onChange={(e) => handleInterestRateChange(e.target.value)}
            onBlur={formatInterestRateOnBlur}
            placeholder="5.90"
            className={
              submitted && !formData.interestRate ? styles.errorInput : ""
            }
          />
          <span className={styles.suffix}>%</span>
        </div>
      </div>

      {/* 🏦 Current Lender */}
      <div className={styles.inputGroup}>
        <label>
          Who is your current lender?{" "}
          <span className={styles.required}>*</span>
        </label>
        <Select
          options={lenderOptions}
          value={
            formData.currentLender
              ? lenderOptions.find(
                  (option) => option.value === formData.currentLender
                )
              : null
          }
          onChange={(selected) =>
            setFormData((prev) => ({
              ...prev,
              currentLender: selected ? selected.value : "",
            }))
          }
          placeholder="Search or select your lender..."
          isClearable
          isSearchable
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          styles={{
            control: (provided) => ({
              ...provided,
              borderRadius: "0.6rem",
              border:
                submitted && !formData.currentLender
                  ? "1.5px solid red"
                  : "1.5px solid #ccc",
              boxShadow: "none",
              minHeight: "3.25rem",
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 4000,
            }),
          }}
        />
      </div>

      {/* 🔘 Navigation */}
      <div className={styles.navBtns}>
        <button
          className={`${styles.nextBtn} ${styles.fullWidthBtn}`}
          onClick={handleNext}
          disabled={!isFormValid}
          style={{
            opacity: isFormValid ? 1 : 0.5,
            cursor: isFormValid ? "pointer" : "not-allowed",
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default LoanDetails;
